import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import api, { getImageUrl } from "@/lib/api";
import { DEFAULT_PROJECTS, getStoredProjects } from "@/lib/projectsData";
import { Menu } from "@/components/Menu";
import { Footer } from "@/components/Footer";
import { useRevealObserver } from "@/hooks/useReveal";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const rootRef = useRevealObserver();

  useEffect(() => {
    window.scrollTo(0, 0);
    setProject(null);

    const localProjects = getStoredProjects();

    api.get("/projects")
      .then((r) => setAllProjects(Array.isArray(r.data) && r.data.length ? r.data : localProjects))
      .catch(() => setAllProjects(localProjects));

    api.get(`/projects/${slug}`)
      .then((r) => {
        if (r.data && typeof r.data === "object" && r.data.title && r.data.slug) setProject(r.data);
        else {
          const fallback = localProjects.find((p) => p.slug === slug);
          if (fallback) setProject(fallback);
          else setNotFound(true);
        }
      })
      .catch(() => {
        const fallback = localProjects.find((p) => p.slug === slug);
        if (fallback) setProject(fallback);
        else setNotFound(true);
      });
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-[#F3F0EA] min-h-screen flex flex-col items-center justify-center gap-6 text-[#111111]">
        <Menu />
        <h1 className="text-4xl uppercase font-medium">Obra no encontrada</h1>
        <Link to="/obras" className="ed-link text-[#D94A1E] uppercase tracking-widest text-sm">Volver a Obras</Link>
      </div>
    );
  }
  if (!project) return <div className="bg-[#F3F0EA] min-h-screen" />;

  const projectsList = allProjects.length > 0 ? allProjects : getStoredProjects();
  const currentIndex = projectsList.findIndex((p) => p.slug === slug);
  const prevProject = currentIndex > -1 ? projectsList[(currentIndex - 1 + projectsList.length) % projectsList.length] : null;
  const nextProject = currentIndex > -1 ? projectsList[(currentIndex + 1) % projectsList.length] : null;

  return (
    <div ref={rootRef} className="bg-[#F3F0EA] text-[#111111] min-h-screen" data-testid="project-detail-page">
      <Menu />
      <header className="px-6 md:px-10 lg:px-16 pt-32 md:pt-40 pb-12">
        <Link to="/obras" data-testid="project-back-link" className="reveal ed-link text-xs uppercase tracking-[0.25em] font-semibold text-[#D94A1E]">← Obras</Link>
        <h1 className="reveal mt-8 text-[12vw] md:text-[8vw] leading-[0.9] tracking-tighter uppercase font-medium max-w-6xl" data-testid="project-title">
          {project.title}
        </h1>
      </header>

      <div className="px-6 md:px-10 lg:px-16 reveal img-zoom">
        <img src={getImageUrl(project.hero_image)} alt={project.title} className="w-full aspect-[16/9] object-cover" data-testid="project-hero-image" />
      </div>

      <section className="px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4 reveal">
          {[
            ["Sector", project.sector],
            ["Ubicación", project.location],
            ["Año", project.year],
            ["Servicios", project.services.join(" · ")],
          ].map(([k, v]) => (
            <div key={k} className="border-t border-[#111111] py-4 grid grid-cols-2 gap-4 text-sm">
              <span className="uppercase tracking-widest text-xs font-semibold pt-[2px]">{k}</span>
              <span className="font-light">{v}</span>
            </div>
          ))}
          <div className="line-reveal border-t border-[#111111]" />
        </div>
        <div className="md:col-span-6 md:col-start-6 reveal" style={{ transitionDelay: "150ms" }}>
          <p className="text-xl md:text-2xl font-light leading-relaxed" data-testid="project-description">{project.description}</p>
        </div>
      </section>

      {project.gallery.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 pb-24 space-y-10 md:space-y-16" data-testid="project-gallery">
          {project.gallery.map((img, i) => (
            <div key={`${img}-${i}`} className={`reveal img-zoom ${i % 2 === 0 ? "md:w-[70%]" : "md:w-[55%] md:ml-auto"}`}>
              <img src={getImageUrl(img)} alt={`${project.title} — imagen ${i + 1}`} loading="lazy" className="w-full object-cover" />
            </div>
          ))}
        </section>
      )}

      {projectsList.length > 1 && (
        <nav className="px-6 md:px-10 lg:px-16 py-16 md:py-24 border-t border-[#111111]" data-testid="project-nav">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            {prevProject && (
              <Link
                to={`/obras/${prevProject.slug}`}
                data-testid="project-nav-prev"
                className="group flex items-center gap-5 p-4 -ml-4 rounded-sm hover:bg-[#111111]/[0.03] transition-colors duration-300"
              >
                <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 overflow-hidden bg-[#111111]/10">
                  <img
                    src={getImageUrl(prevProject.hero_image)}
                    alt={prevProject.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D94A1E] flex items-center gap-1">
                    <span aria-hidden>←</span> Obra Anterior
                  </span>
                  <h4 className="mt-1 text-xl md:text-2xl font-medium uppercase tracking-tight truncate group-hover:text-[#D94A1E] transition-colors duration-300">
                    {prevProject.title}
                  </h4>
                  <span className="mt-1 text-xs font-light text-[#111111]/60">{prevProject.sector}</span>
                </div>
              </Link>
            )}

            {nextProject && (
              <Link
                to={`/obras/${nextProject.slug}`}
                data-testid="project-nav-next"
                className="group flex items-center justify-end text-right gap-5 p-4 -mr-4 rounded-sm hover:bg-[#111111]/[0.03] transition-colors duration-300 md:col-start-2"
              >
                <div className="flex flex-col min-w-0 items-end">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D94A1E] flex items-center gap-1">
                    Obra Siguiente <span aria-hidden>→</span>
                  </span>
                  <h4 className="mt-1 text-xl md:text-2xl font-medium uppercase tracking-tight truncate group-hover:text-[#D94A1E] transition-colors duration-300">
                    {nextProject.title}
                  </h4>
                  <span className="mt-1 text-xs font-light text-[#111111]/60">{nextProject.sector}</span>
                </div>
                <div className="w-20 h-20 md:w-24 md:h-24 shrink-0 overflow-hidden bg-[#111111]/10">
                  <img
                    src={getImageUrl(nextProject.hero_image)}
                    alt={nextProject.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </Link>
            )}
          </div>
        </nav>
      )}

      <Footer />
    </div>
  );
}
